import {
  AlertLevel,
  QuestionRuleInstance,
  SchedulerInterval,
} from '@jupiterone/jupiter-types';
import {
  JupiterTheme,
  KeyValueInput,
  QuestionRuleInstanceWithTags,
} from '@jupiterone/jupiter-web-toolkit';
import AutocompleteTagsSelect from 'jupiter-web-alerts/src/components/Rules/AutocompleteTagsSelect';
import {
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import get from 'lodash/get';
import React, { ChangeEvent } from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import useRuleValidation from 'jupiter-web-alerts/src/components/Rules/useRuleValidation';
import useBasicRuleFieldsLogic from './useBasicRuleFieldsLogic';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    templateValue: { paddingLeft: theme.spacing(0.5) },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      marginTop: theme.spacing(-0.75),
    },
    inputRow: {
      maxWidth: '100%',
      display: 'grid',
      gridGap: `0 ${theme.spacing(2)}px`,
      gridTemplateColumns: '1fr 1fr',
      padding: 0,
      '& + $inputRow': {
        marginTop: theme.spacing(2),
      },
      '& + $inputQuery': {
        margin: theme.spacing(4, 0),
      },
    },
    inputDescription: {
      gridTemplateColumns: '1fr',
    },
    inputQuery: {
      gridTemplateColumns: '1fr',
    },
    tags: {
      gridTemplateColumns: '1fr',
    },
    focused: {
      '& + $textarea': {
        background: theme.palette.coolGrey[50],
      },
    },
    textarea: {
      background: 'rgba(239, 240, 245, 0.5)',
      '&:hover': {
        background: theme.palette.coolGrey[50],
      },
      '& textarea': {
        background: 'none',
        color: theme.palette.primary.main,
        fontFamily: `'Ubuntu Mono', monospace`,
      },
    },
    label: {
      display: 'flex',
    },
    tagLabel: {
      paddingBottom: theme.spacing(1),
    },
  })
);

const createHelperText = (rule: Partial<QuestionRuleInstance>) => {
  let intervalText = '';
  switch (rule.pollingInterval) {
    case SchedulerInterval.DISABLED:
      return 'This disabled custom rule will NOT be evaluated on an interval.';

    case SchedulerInterval.THIRTY_MINUTES:
      intervalText = 'every thirty minutes';
      break;
    case SchedulerInterval.ONE_HOUR:
      intervalText = 'every hour';
      break;
    case SchedulerInterval.FOUR_HOURS:
      intervalText = 'every four hours';
      break;
    case SchedulerInterval.EIGHT_HOURS:
      intervalText = 'every eight hours';
      break;
    case SchedulerInterval.TWELVE_HOURS:
      intervalText = 'every twelve hours';
      break;
    case SchedulerInterval.ONE_DAY:
      intervalText = 'every day';
      break;
    case SchedulerInterval.ONE_WEEK:
      intervalText = 'every week';
      break;

    default:
      intervalText = 'per polling interval';
  }

  return `The custom rule will be evaluated ${intervalText}. If the query you entered returns at least one match, it will trigger an alert.`;
};

export interface BasicRuleFieldsProps {
  readonly?: boolean;
  rule: QuestionRuleInstanceWithTags;
  tags?: string[];
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onTagChange: (editedRule: QuestionRuleInstanceWithTags) => void;
}

const BasicRuleFields = ({
  rule,
  tags,
  readonly,
  onChange,
  onTagChange,
}: BasicRuleFieldsProps) => {
  const classes = useStyles();
  const validation = useRuleValidation(rule);
  const { selectedTags, setSelectedTags } = useBasicRuleFieldsLogic({
    rule,
    onTagChange,
  });

  return (
    <>
      <div className={classes.inputRow}>
        <TextField
          disabled={readonly}
          label="Name"
          value={rule.name}
          onChange={onChange}
          name={'name'}
          error={validation.name.dirty && validation.name.error}
          helperText={validation.name.dirty && validation.name.helpText}
        />
        <TextField
          disabled={readonly}
          label="Severity"
          value={get(rule, 'operations[0].actions[0].targetValue', '')}
          onChange={onChange}
          name={'severity'}
          select={true}
        >
          {Object.values(AlertLevel).map((level: AlertLevel) => (
            <MenuItem key={level} value={level}>
              {level}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <div className={clsx(classes.inputRow, classes.inputDescription)}>
        <TextField
          disabled={readonly}
          label="Description"
          value={rule.description}
          onChange={onChange}
          name={'description'}
        />
      </div>
      <div className={clsx(classes.inputRow, classes.tags)}>
        <Typography className={classes.tagLabel} color="inherit">
          Tags
        </Typography>
        <AutocompleteTagsSelect
          disabled={readonly}
          selectedTags={selectedTags}
          onChange={setSelectedTags}
          items={tags?.map((tag) => ({ id: tag, title: tag })) ?? []}
        />
      </div>
      <div className={clsx(classes.inputRow, classes.inputQuery)}>
        <TextField
          disabled={readonly}
          label="Query"
          variant={'filled'}
          multiline={true}
          rows={5}
          value={get(rule, 'question.queries[0].query', '')}
          helperText={createHelperText(rule)}
          InputProps={{
            classes: {
              root: classes.textarea,
            },
          }}
          InputLabelProps={{
            classes: {
              focused: classes.focused,
            },
          }}
          onChange={onChange}
          name={'query'}
        />
      </div>
      <div className={clsx(classes.inputRow, classes.inputQuery)}>
        <KeyValueInput
          disabled={readonly}
          label={
            <div className={classes.label}>
              <Typography color="inherit">Templates</Typography>
              <Tooltip
                title="Templates can be used inside of any property under the operations property on a rule. The templates can contain a JavaScript-like syntax that automatically have input variables injected for usage."
                placement="bottom"
              >
                <IconButton className={classes.iconButton}>
                  <HelpIcon size={20} />
                </IconButton>
              </Tooltip>
            </div>
          }
          error={validation.templates.dirty && validation.templates.error}
          helperText={validation.templates.helpText}
          initialValues={get(rule, 'templates', {})}
          onChange={(templates: { [key: string]: string | number }) => {
            onChange({
              target: { name: 'templates', value: templates },
            } as any);
          }}
          valueInputProps={{
            disabled: readonly,
            multiline: true,
            rows: 1,
            InputProps: {
              classes: { root: clsx(classes.textarea, classes.templateValue) },
            },
          }}
        />
      </div>
    </>
  );
};
export default BasicRuleFields;
